import { supabase } from "../lib/supabase";
import { logSupabaseError } from './errorLogService';

/**
 * Fetch email template from database
 * @param {string} templateType - Type of template to fetch
 * @returns {Object|null} Template data or null if not found
 */
const fetchEmailTemplate = async (templateType) => {
  try {
    const { data, error } = await supabase
      ?.from('email_templates')
      ?.select('*')
      ?.eq('template_type', templateType)
      ?.eq('is_active', true)
      ?.single();

    if (error) {
      // PGRST116 means no rows found - this is expected for deleted templates
      if (error?.code === 'PGRST116') {
        return null;
      }
      
      // Log error to centralized error logging system
      await logSupabaseError({
        error,
        query: 'fetchEmailTemplate',
        table: 'email_templates',
        operation: 'SELECT',
        componentName: 'emailService',
        functionName: 'fetchEmailTemplate',
        additionalMetadata: { templateType }
      });
      
      throw error;
    }
    return data;
  } catch (error) {
    console.error(`Error fetching template ${templateType}:`, error);
    return null;
  }
};

/**
 * Replace placeholders in template content
 * @param {string} content - Template content with placeholders
 * @param {Object} variables - Variables to replace
 * @returns {string} Content with replaced placeholders
 */
const replacePlaceholders = (content, variables) => {
  let result = content;
  
  // STEP 1: Replace all variable placeholders FIRST
  Object.entries(variables)?.forEach(([key, value]) => {
    const regex = new RegExp(`{{${key}}}`, 'g');
    result = result?.replace(regex, value || '');
  });

  // STEP 2: Handle conditional sections AFTER variable replacement
  // Remove conditional blocks where the variable was empty/falsy
  Object.entries(variables)?.forEach(([key, value]) => {
    if (!value) {
      // Remove the entire {{#if variable}}...{{/if}} block if variable is falsy
      const regex = new RegExp(`{{#if ${key}}}[\\s\\S]*?{{/if}}`, 'g');
      result = result?.replace(regex, '');
    }
  });

  // STEP 3: Remove remaining conditional tags (for variables that had values)
  result = result?.replace(/{{#if \w+}}/g, '');
  result = result?.replace(/{{\/if}}/g, ''); // Fixed: proper escape for closing tag
  result = result?.replace(/{{\\\/if}}/g, ''); // Handle double-escaped version
  result = result?.replace(/{{\\\\\/if}}/g, ''); // Handle quad-escaped version
  result = result?.replace(/{{\\\\\\\\\/if}}/g, ''); // Handle octo-escaped version

  return result;
};

/**
 * Send signup confirmation email
 * @param {Object} dealershipData - Dealership registration data
 */
export const sendSignupConfirmation = async (dealershipData) => {
  try {
    // Fetch custom template from database
    const template = await fetchEmailTemplate('signup_confirmation');
    
    let subject = 'Willkommen bei THG-Prämie - Registrierung erfolgreich';
    let htmlContent = '';
    let qrCodeData = null;

    // Generate QR code data for dealership landing page
    if (dealershipData?.urlSlug) {
      qrCodeData = `https://www.gridbalance.de/${dealershipData?.urlSlug}`;
    }

    if (template) {
      // Use custom template
      const templateVariables = {
        ...dealershipData,
        qrCodeData
      };
      subject = replacePlaceholders(template?.subject, templateVariables);
      htmlContent = replacePlaceholders(template?.html_content, templateVariables);
    }

    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: {
        type: template ? 'custom' : 'signup_confirmation',
        to: dealershipData?.email,
        data: {
          dealershipName: dealershipData?.name,
          contactPerson: dealershipData?.contact_person,
          email: dealershipData?.email,
          qrCodeData
        },
        ...(template && { customSubject: subject, customHtml: htmlContent })
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send signup confirmation:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send status update email to customer
 * @param {Object} statusData - Status update data
 */
export const sendStatusUpdate = async (statusData) => {
  try {
    // Fetch custom template from database
    const template = await fetchEmailTemplate('status_update');
    
    let subject = `Status-Update für Ihren THG-Prämienantrag #${statusData?.applicationId}`;
    let htmlContent = '';
    let qrCodeData = null;

    // Generate QR code for application tracking or verification
    if (statusData?.applicationId) {
      qrCodeData = `https://www.gridbalance.de/application/${statusData?.applicationId}`;
    }

    if (template) {
      // Use custom template
      const templateVariables = {
        ...statusData,
        qrCodeData
      };
      subject = replacePlaceholders(template?.subject, templateVariables);
      htmlContent = replacePlaceholders(template?.html_content, templateVariables);
    }

    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: {
        type: template ? 'custom' : 'status_update',
        to: statusData?.customerEmail,
        data: {
          dealershipName: statusData?.dealershipName,
          status: statusData?.status,
          applicationId: statusData?.applicationId,
          notes: statusData?.notes,
          message: statusData?.message,
          qrCodeData
        },
        ...(template && { customSubject: subject, customHtml: htmlContent })
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send status update:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send bulk emails with placeholder variable support
 * @param {Object} bulkEmailData - Bulk email configuration
 * @param {Array} bulkEmailData.recipients - Array of recipients with email and variables
 * @param {string} bulkEmailData.subject - Email subject (supports {{placeholders}})
 * @param {string} bulkEmailData.htmlContent - Email HTML content (supports {{placeholders}})
 * 
 * Example recipients format:
 * [
 *   { email: "dealer1@example.com", variables: { dealershipName: "Dealer 1", status: "approved" } },
 *   { email: "dealer2@example.com", variables: { dealershipName: "Dealer 2", status: "pending" } }
 * ]
 */
export const sendBulkEmails = async (bulkEmailData) => {
  try {
    const { recipients, subject, htmlContent } = bulkEmailData;

    if (!recipients || !Array.isArray(recipients) || recipients?.length === 0) {
      throw new Error('Recipients array is required');
    }

    if (!subject || !htmlContent) {
      throw new Error('Subject and HTML content are required');
    }

    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: {
        type: 'bulk',
        recipients,
        data: {
          subject,
          htmlContent
        }
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send bulk emails:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send email to thg@gridbalance.de with custom content
 * @param {string} subject - Email subject
 * @param {string} htmlContent - Email HTML content
 */
export const sendToGridBalance = async (subject, htmlContent) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: {
        type: 'bulk',
        recipients: [
          { 
            email: 'thg@gridbalance.de', 
            variables: {} 
          }
        ],
        data: {
          subject,
          htmlContent
        }
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send email to Grid Balance:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send vehicle documents to oliver.wagner@gridbalance.de
 * @param {Array} applications - Array of selected applications with vehicle documents
 * @returns {Object} Result of email send operation
 */
export const sendVehicleDocumentsToOliver = async (applications) => {
  try {
    if (!applications || applications?.length === 0) {
      throw new Error('No applications provided');
    }

    // Filter applications that have vehicle documents
    const appsWithDocs = applications?.filter(app => app?.documentUrl);

    if (appsWithDocs?.length === 0) {
      throw new Error('No applications with vehicle documents found');
    }

    // Build email content with document links
    let htmlContent = `
      <h2>Fahrzeugscheine - Antragsbearbeitung</h2>
      <p>Anbei die Fahrzeugscheine für die ausgewählten Anträge:</p>
      <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
        <thead>
          <tr style="background-color: #f3f4f6;">
            <th style="padding: 12px; text-align: left; border: 1px solid #e5e7eb;">Kunde</th>
            <th style="padding: 12px; text-align: left; border: 1px solid #e5e7eb;">Fahrgestellnummer</th>
            <th style="padding: 12px; text-align: left; border: 1px solid #e5e7eb;">Fahrzeugmodell</th>
            <th style="padding: 12px; text-align: left; border: 1px solid #e5e7eb;">Dokument</th>
          </tr>
        </thead>
        <tbody>
    `;

    for (const app of appsWithDocs) {
      // Create signed URL with 7 days expiration for email access
      const { data, error } = await supabase?.storage?.from('vehicle-documents')?.createSignedUrl(app?.documentUrl, 604800); // 7 days in seconds
      const documentUrl = data?.signedUrl || '#';

      if (error) {
        console.error('Error creating signed URL:', error);
      }

      htmlContent += `
        <tr>
          <td style="padding: 12px; border: 1px solid #e5e7eb;">${app?.customerName || 'N/A'}</td>
          <td style="padding: 12px; border: 1px solid #e5e7eb;">${app?.chassisNumber || 'N/A'}</td>
          <td style="padding: 12px; border: 1px solid #e5e7eb;">${app?.vehicleModel || 'N/A'}</td>
          <td style="padding: 12px; border: 1px solid #e5e7eb;">
            <a href="${documentUrl}" style="color: #2563eb; text-decoration: underline;">Dokument anzeigen</a>
          </td>
        </tr>
      `;
    }

    htmlContent += `
        </tbody>
      </table>
      <p style="margin-top: 20px; color: #6b7280; font-size: 14px;">
        Insgesamt ${appsWithDocs?.length} Dokument(e) verfügbar.
      </p>
    `;

    let subject = `Fahrzeugscheine - ${appsWithDocs?.length} Antrag/Anträge`;

    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: {
        type: 'bulk',
        recipients: [
          { 
            email: 'oliver.wagner@gridbalance.de', 
            variables: {} 
          }
        ],
        data: {
          subject,
          htmlContent
        }
      }
    });

    if (error) throw error;
    return { success: true, data, count: appsWithDocs?.length };
  } catch (error) {
    console.error('Failed to send vehicle documents to Oliver:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send voucher notification email
 * @param {Object} voucherData - Voucher notification data
 * @param {string} voucherData.email - Recipient email address
 * @param {string} voucherData.dealershipName - Name of the dealership
 * @param {string} voucherData.voucherCode - Generated voucher code
 * @param {number} voucherData.amount - Voucher amount
 * @param {string} voucherData.applicationId - Related application ID
 * @param {string} voucherData.vehicleInfo - Vehicle information
 * @param {string} voucherData.expiryDate - Optional expiry date
 * @param {string} voucherData.message - Optional custom message
 */
export const sendVoucherNotification = async (voucherData) => {
  try {
    // Fetch custom template from database
    const template = await fetchEmailTemplate('voucher_notification');
    
    let subject = 'Ihr THG-Prämien-Gutschein ist verfügbar';
    let htmlContent = '';

    if (template) {
      // Use custom template
      subject = replacePlaceholders(template?.subject, voucherData);
      htmlContent = replacePlaceholders(template?.html_content, voucherData);
    }

    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: {
        type: 'voucher_notification',
        to: voucherData?.email,
        data: {
          dealershipName: voucherData?.dealershipName,
          voucherCode: voucherData?.voucherCode,
          amount: voucherData?.amount,
          applicationId: voucherData?.applicationId,
          vehicleInfo: voucherData?.vehicleInfo,
          expiryDate: voucherData?.expiryDate,
          message: voucherData?.message
        },
        ...(template && { customSubject: subject, customHtml: htmlContent })
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send voucher notification:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send application accepted email (status changed to processing)
 * @param {Object} applicationData - Application data
 * @returns {Object} Result of email send operation
 */
export const sendApplicationAcceptedEmail = async (applicationData) => {
  try {
    // Fetch full application data including license plate from database
    const { data: fullAppData, error: fetchError } = await supabase
      ?.from('applications')
      ?.select('customer_license_plate')
      ?.eq('id', applicationData?.applicationId || applicationData?.id)
      ?.single();

    if (fetchError) {
      console.error('Error fetching application data:', fetchError);
    }

    // Fetch custom template from database
    const template = await fetchEmailTemplate('application_accepted');
    
    const templateVariables = {
      applicationId: applicationData?.applicationId || applicationData?.id,
      dealershipName: applicationData?.dealershipName || 'Ihr Partner',
      customerName: applicationData?.customerName || 'Kunde',
      vehicleModel: applicationData?.vehicleModel || '',
      customerLicensePlate: fullAppData?.customer_license_plate || applicationData?.customerLicensePlate || 'Nicht angegeben'
    };

    let subject, htmlContent;

    if (template) {
      // Use custom template from database
      subject = replacePlaceholders(template?.subject, templateVariables);
      htmlContent = replacePlaceholders(template?.html_content, templateVariables);
    } else {
      // Use fallback template when database template doesn't exist
      console.log('Using fallback template for application_accepted');
      subject = 'Ihr THG-Antrag wurde angenommen und ist in Prüfung';
      htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Antrag angenommen</title>
        </head>
        <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
          <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px;">
            <tr>
              <td align="center">
                <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                  <!-- Header -->
                  <tr>
                    <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 30px; text-align: center;">
                      <h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: bold;">Antrag angenommen und in Prüfung</h1>
                    </td>
                  </tr>
                  
                  <!-- Content -->
                  <tr>
                    <td style="padding: 40px 30px;">
                      <p style="margin: 0 0 20px; color: #333333; font-size: 16px; line-height: 1.6;">
                        Sehr geehrte/r ${templateVariables?.customerName},
                      </p>
                      
                      <p style="margin: 0 0 20px; color: #333333; font-size: 16px; line-height: 1.6;">
                        vielen Dank für Ihren THG-Prämienantrag! Wir freuen uns, Ihnen mitteilen zu können, dass Ihr Antrag <strong>angenommen wurde und sich nun in Prüfung befindet</strong>.
                      </p>
                      
                      <div style="background-color: #f8f9fa; border-left: 4px solid #667eea; padding: 20px; margin: 30px 0; border-radius: 4px;">
                        <p style="margin: 0 0 10px; color: #333333; font-size: 14px;"><strong>Antragsnummer:</strong> ${templateVariables?.applicationId}</p>
                        <p style="margin: 0 0 10px; color: #333333; font-size: 14px;"><strong>Partner:</strong> ${templateVariables?.dealershipName}</p>
                        ${templateVariables?.vehicleModel ? `<p style="margin: 0; color: #333333; font-size: 14px;"><strong>Fahrzeug:</strong> ${templateVariables?.vehicleModel}</p>` : ''}
                      </div>
                      
                      <h2 style="margin: 30px 0 15px; color: #333333; font-size: 20px; font-weight: bold;">Wie geht es weiter?</h2>
                      
                      <ol style="margin: 0 0 20px; padding-left: 20px; color: #333333; font-size: 16px; line-height: 1.8;">
                        <li style="margin-bottom: 10px;">Wir prüfen Ihre Unterlagen sorgfältig</li>
                        <li style="margin-bottom: 10px;">Sie erhalten eine Benachrichtigung über das Ergebnis</li>
                        <li style="margin-bottom: 10px;">Bei Genehmigung wird Ihr Gutscheincode generiert</li>
                      </ol>
                      
                      <p style="margin: 20px 0; color: #333333; font-size: 16px; line-height: 1.6;">
                        Die Bearbeitung dauert in der Regel <strong>3-5 Werktage</strong>. Sie werden per E-Mail über den Status Ihres Antrags informiert.
                      </p>
                      
                      <p style="margin: 30px 0 0; color: #666666; font-size: 14px; line-height: 1.6;">
                        Bei Fragen stehen wir Ihnen gerne zur Verfügung.
                      </p>
                      
                      <p style="margin: 20px 0 0; color: #666666; font-size: 14px; line-height: 1.6;">
                        Mit freundlichen Grüßen<br>
                        <strong>Ihr THG-Prämie Team</strong><br>
                        ${templateVariables?.dealershipName}
                      </p>
                    </td>
                  </tr>
                  
                  <!-- Footer -->
                  <tr>
                    <td style="background-color: #f8f9fa; padding: 20px 30px; text-align: center; border-top: 1px solid #e9ecef;">
                      <p style="margin: 0; color: #6c757d; font-size: 12px; line-height: 1.5;">
                        Diese E-Mail wurde automatisch generiert. Bitte antworten Sie nicht auf diese E-Mail.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </body>
        </html>
      `;
    }

    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: {
        type: 'custom',
        to: applicationData?.customerEmail,
        customSubject: subject,
        customHtml: htmlContent,
        data: templateVariables
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send application accepted email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send application approved email with voucher code
 * @param {Object} applicationData - Application data
 * @returns {Object} Result of email send operation
 */
export const sendApplicationApprovedEmail = async (applicationData) => {
  try {
    console.log('========================================');
    console.log('[sendApplicationApprovedEmail] ========== STARTING APPROVED EMAIL WORKFLOW ==========');
    console.log('[sendApplicationApprovedEmail] Timestamp:', new Date()?.toISOString());
    console.log('[sendApplicationApprovedEmail] Input Data:', JSON.stringify(applicationData, null, 2));
    console.log('========================================');

    const createVoucher = async (applicationId, dealershipId) => {
      try {
        console.log('[createVoucher] ========== VOUCHER CREATION START ==========');
        console.log('[createVoucher] Application ID:', applicationId);
        console.log('[createVoucher] Dealership ID:', dealershipId);

        // Check if voucher already exists
        const { data: existingVoucher, error: checkError } = await supabase
          ?.from('vouchers')
          ?.select('voucher_code')
          ?.eq('application_id', applicationId)
          ?.single();

        if (!checkError && existingVoucher) {
          console.log('[createVoucher] ✅ Voucher already exists:', existingVoucher?.voucher_code);
          console.log('[createVoucher] ========== VOUCHER CREATION END (EXISTING) ==========');
          return { success: true, voucher: existingVoucher };
        }

        console.log('[createVoucher] No existing voucher found, creating new one...');
        
        // Generate voucher code in format xxxx-xxxx-xxxx (exactly 12 characters)
        const parts = [];
        for (let i = 0; i < 3; i++) {
          let part = '';
          for (let j = 0; j < 4; j++) {
            part += Math.floor(Math.random() * 10)?.toString();
          }
          parts?.push(part);
        }
        let voucherCode = parts?.join('-');
        console.log('[createVoucher] Generated voucher code:', voucherCode);

        // Calculate expiry date (1 year from now)
        const expiryDate = new Date();
        expiryDate?.setFullYear(expiryDate?.getFullYear() + 1);

        const { data, error } = await supabase
          ?.from('vouchers')
          ?.insert({
            application_id: applicationId,
            dealership_id: dealershipId,
            voucher_code: voucherCode,
            is_redeemed: false,
            expiry_date: expiryDate?.toISOString(),
            created_at: new Date()?.toISOString()
          })
          ?.select()
          ?.single();

        if (error) {
          console.error('[createVoucher] ❌ Database error creating voucher:', error);
          console.error('[createVoucher] Error details:', JSON.stringify(error, null, 2));
          console.log('[createVoucher] ========== VOUCHER CREATION END (ERROR) ==========');
          return { success: false, error: error?.message };
        }

        console.log('[createVoucher] ✅ Voucher created successfully in database');
        console.log('[createVoucher] Voucher data:', JSON.stringify(data, null, 2));
        console.log('[createVoucher] ========== VOUCHER CREATION END (SUCCESS) ==========');
        return { success: true, voucher: data };
      } catch (err) {
        console.error('[createVoucher] ❌ Exception creating voucher:', err);
        console.error('[createVoucher] Exception details:', JSON.stringify(err, null, 2));
        console.log('[createVoucher] ========== VOUCHER CREATION END (EXCEPTION) ==========');
        return { success: false, error: err?.message };
      }
    };

    // Create voucher first
    const applicationId = applicationData?.id || applicationData?.applicationId;
    const dealershipId = applicationData?.dealershipId;

    console.log('[sendApplicationApprovedEmail] Extracted IDs:');
    console.log('[sendApplicationApprovedEmail]   - Application ID:', applicationId);
    console.log('[sendApplicationApprovedEmail]   - Dealership ID:', dealershipId);

    if (!applicationId || !dealershipId) {
      console.error('[sendApplicationApprovedEmail] ❌ MISSING REQUIRED IDS');
      console.error('[sendApplicationApprovedEmail]   - applicationId:', applicationId);
      console.error('[sendApplicationApprovedEmail]   - dealershipId:', dealershipId);
      console.log('[sendApplicationApprovedEmail] ========== WORKFLOW END (MISSING IDS) ==========');
      return { success: false, error: 'Missing application ID or dealership ID' };
    }

    console.log('[sendApplicationApprovedEmail] Calling createVoucher...');
    const voucherResult = await createVoucher(applicationId, dealershipId);

    if (!voucherResult?.success) {
      console.error('[sendApplicationApprovedEmail] ❌ Failed to create voucher');
      console.error('[sendApplicationApprovedEmail] Voucher error:', voucherResult?.error);
      console.log('[sendApplicationApprovedEmail] ========== WORKFLOW END (VOUCHER FAILED) ==========');
      throw new Error('Failed to create voucher: ' + voucherResult?.error);
    }

    let voucherCode = voucherResult?.voucher?.voucher_code;
    console.log('[sendApplicationApprovedEmail] ✅ Voucher ready:', voucherCode);

    // Fetch full application data including license plate from database
    console.log('[sendApplicationApprovedEmail] Fetching full application data...');
    const { data: fullAppData, error: fetchError } = await supabase
      ?.from('applications')
      ?.select('customer_license_plate')
      ?.eq('id', applicationId)
      ?.single();

    if (fetchError) {
      console.error('[sendApplicationApprovedEmail] ⚠️ Error fetching application data:', fetchError);
    } else {
      console.log('[sendApplicationApprovedEmail] Application data fetched:', JSON.stringify(fullAppData, null, 2));
    }

    // Fetch custom template from database
    console.log('[sendApplicationApprovedEmail] Fetching email template: application_approved');
    const template = await fetchEmailTemplate('application_approved');

    if (!template) {
      console.error('[sendApplicationApprovedEmail] ❌ TEMPLATE NOT FOUND');
      console.error('[sendApplicationApprovedEmail] Template type: application_approved');
      console.error('[sendApplicationApprovedEmail] Please check:');
      console.error('[sendApplicationApprovedEmail]   1. Template exists in email_templates table');
      console.error('[sendApplicationApprovedEmail]   2. Template is_active = true');
      console.error('[sendApplicationApprovedEmail]   3. Migration 20260212121000_fix_approved_email_workflow.sql was applied');
      console.log('[sendApplicationApprovedEmail] ========== WORKFLOW END (TEMPLATE NOT FOUND) ==========');
      return { success: false, error: 'Template not found' };
    }

    console.log('[sendApplicationApprovedEmail] ✅ Template found:', template?.template_name);
    console.log('[sendApplicationApprovedEmail] Template ID:', template?.id);
    console.log('[sendApplicationApprovedEmail] Template is_active:', template?.is_active);

    const templateVariables = {
      applicationId: applicationId,
      dealershipName: applicationData?.dealershipName || 'Ihr Partner',
      customerName: applicationData?.customerName || 'Kunde',
      voucherCode: voucherCode,
      customerLicensePlate: fullAppData?.customer_license_plate || applicationData?.customerLicensePlate || 'Nicht angegeben'
    };

    console.log('[sendApplicationApprovedEmail] Template variables:', JSON.stringify(templateVariables, null, 2));

    let subject = replacePlaceholders(template?.subject, templateVariables);
    let htmlContent = replacePlaceholders(template?.html_content, templateVariables);

    console.log('[sendApplicationApprovedEmail] Email subject:', subject);
    console.log('[sendApplicationApprovedEmail] Email recipient:', applicationData?.customerEmail);
    console.log('[sendApplicationApprovedEmail] Calling edge function: send-email');

    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: {
        type: 'custom',
        to: applicationData?.customerEmail,
        customSubject: subject,
        customHtml: htmlContent,
        data: templateVariables
      }
    });

    if (error) {
      console.error('[sendApplicationApprovedEmail] ❌ EDGE FUNCTION ERROR');
      console.error('[sendApplicationApprovedEmail] Error:', JSON.stringify(error, null, 2));
      console.error('[sendApplicationApprovedEmail] Please check:');
      console.error('[sendApplicationApprovedEmail]   1. Edge function is deployed: supabase functions deploy send-email --no-verify-jwt');
      console.error('[sendApplicationApprovedEmail]   2. GMAIL_USER secret is set in Supabase');
      console.error('[sendApplicationApprovedEmail]   3. GMAIL_APP_PASSWORD secret is set in Supabase');
      console.log('[sendApplicationApprovedEmail] ========== WORKFLOW END (EDGE FUNCTION ERROR) ==========');
      throw error;
    }

    console.log('[sendApplicationApprovedEmail] ✅ ✅ ✅ EMAIL SENT SUCCESSFULLY ✅ ✅ ✅');
    console.log('[sendApplicationApprovedEmail] Edge function response:', JSON.stringify(data, null, 2));
    console.log('[sendApplicationApprovedEmail] Voucher code sent:', voucherCode);
    console.log('[sendApplicationApprovedEmail] ========== WORKFLOW END (SUCCESS) ==========');
    console.log('========================================');
    return { success: true, data, voucherCode };
  } catch (error) {
    console.error('========================================');
    console.error('[sendApplicationApprovedEmail] ❌ ❌ ❌ FATAL ERROR ❌ ❌ ❌');
    console.error('[sendApplicationApprovedEmail] Error:', error);
    console.error('[sendApplicationApprovedEmail] Error message:', error?.message);
    console.error('[sendApplicationApprovedEmail] Error stack:', error?.stack);
    console.error('[sendApplicationApprovedEmail] ========== WORKFLOW END (FATAL ERROR) ==========');
    console.error('========================================');
    return { success: false, error: error?.message };
  }
};

/**
 * Send application rejected email
 * @param {Object} applicationData - Application data
 * @returns {Object} Result of email send operation
 */
export const sendApplicationRejectedEmail = async (applicationData) => {
  try {
    // Fetch full application data including license plate from database
    const { data: fullAppData, error: fetchError } = await supabase
      ?.from('applications')
      ?.select('customer_license_plate')
      ?.eq('id', applicationData?.id || applicationData?.applicationId)
      ?.single();

    if (fetchError) {
      console.error('Error fetching application data:', fetchError);
    }

    // Fetch custom template from database
    const template = await fetchEmailTemplate('application_rejected');
    
    if (!template) {
      console.error('Template application_rejected not found in database');
      return { success: false, error: 'Template not found' };
    }

    const templateVariables = {
      applicationId: applicationData?.applicationId || applicationData?.id,
      dealershipName: applicationData?.dealershipName || 'Ihr Partner',
      customerName: applicationData?.customerName || 'Kunde',
      rejectionReason: applicationData?.rejectionReason || '',
      customerLicensePlate: fullAppData?.customer_license_plate || applicationData?.customerLicensePlate || 'Nicht angegeben'
    };

    let subject = replacePlaceholders(template?.subject, templateVariables);
    let htmlContent = replacePlaceholders(template?.html_content, templateVariables);

    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: {
        type: 'custom',
        to: applicationData?.customerEmail,
        customSubject: subject,
        customHtml: htmlContent,
        data: templateVariables
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send application rejected email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send dealership registration email with URLs
 * @param {Object} dealershipData - Dealership registration data
 * @param {string} dealershipData.email - Dealership email address
 * @param {string} dealershipData.dealershipName - Dealership name
 * @param {string} dealershipData.contactPerson - Contact person name
 * @param {string} dealershipData.city - City
 * @param {string} dealershipData.landingPageUrl - Landing page URL
 * @param {string} dealershipData.voucherCheckUrl - Voucher check URL
 * @param {string} dealershipData.urlSlug - URL slug for QR code generation
 */
export const sendDealershipRegistration = async (dealershipData) => {
  try {
    // Fetch custom template from database
    const template = await fetchEmailTemplate('dealership_registration');
    
    let subject = 'Willkommen bei THG-Prämie - Ihre Zugangsdaten';
    let htmlContent = '';
    let qrCodeData = null;

    // Generate QR code data for dealership landing page
    if (dealershipData?.urlSlug) {
      qrCodeData = dealershipData?.landingPageUrl || `https://www.gridbalance.de/${dealershipData?.urlSlug}`;
    }

    if (template) {
      // Use custom template
      const templateVariables = {
        ...dealershipData,
        qrCodeData
      };
      subject = replacePlaceholders(template?.subject, templateVariables);
      htmlContent = replacePlaceholders(template?.html_content, templateVariables);
    }

    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: {
        type: template ? 'custom' : 'dealership_registration',
        to: dealershipData?.email,
        data: {
          dealershipName: dealershipData?.dealershipName || dealershipData?.name,
          contactPerson: dealershipData?.contactPerson || dealershipData?.contact_person,
          email: dealershipData?.email,
          city: dealershipData?.city,
          landingPageUrl: dealershipData?.landingPageUrl,
          voucherCheckUrl: dealershipData?.voucherCheckUrl,
          qrCodeData
        },
        ...(template && { customSubject: subject, customHtml: htmlContent })
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send dealership registration email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Manually trigger renewal emails (for testing or manual execution)
 * Calls the send-renewal-emails Edge Function
 * @returns {Object} Result of renewal email sending operation
 */
export const triggerRenewalEmails = async () => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-renewal-emails', {
      method: 'POST',
      body: {}
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to trigger renewal emails:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send admin notification for new application
 * @param {Object} applicationData - Application data
 * @returns {Object} Result of email send operation
 */
export const sendAdminNewApplicationNotification = async (applicationData) => {
  try {
    // Fetch custom template from database
    const template = await fetchEmailTemplate('admin_new_application');
    
    let subject = `Neuer THG-Prämienantrag eingegangen - ${applicationData?.customerName}`;
    let htmlContent = '';

    if (template) {
      // Use custom template
      const templateVariables = {
        applicationId: applicationData?.applicationId,
        customerName: applicationData?.customerName,
        customerEmail: applicationData?.customerEmail,
        customerPhone: applicationData?.customerPhone || '',
        customerLicensePlate: applicationData?.customerLicensePlate || '',
        dealershipName: applicationData?.dealershipName || 'Nicht zugeordnet',
        chassisNumber: applicationData?.chassisNumber || '',
        submissionDate: new Date()?.toLocaleDateString('de-DE', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        })
      };
      subject = replacePlaceholders(template?.subject, templateVariables);
      htmlContent = replacePlaceholders(template?.html_content, templateVariables);
    } else {
      // Fallback to hardcoded template
      htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #2563eb;">Neuer Antrag eingegangen</h2>
          <p>Ein neuer THG-Prämienantrag wurde eingereicht:</p>
          
          <div style="background-color: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #1f2937;">Antragsinformationen</h3>
            <p><strong>Antrags-ID:</strong> ${applicationData?.applicationId}</p>
            <p><strong>Kunde:</strong> ${applicationData?.customerName}</p>
            <p><strong>E-Mail:</strong> ${applicationData?.customerEmail}</p>
            <p><strong>Telefon:</strong> ${applicationData?.customerPhone || 'Nicht angegeben'}</p>
            <p><strong>Partner:</strong> ${applicationData?.dealershipName || 'Nicht zugeordnet'}</p>
            <p><strong>Fahrgestellnummer:</strong> ${applicationData?.chassisNumber || 'Noch nicht erfasst'}</p>
            <p><strong>Eingangsdatum:</strong> ${new Date()?.toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            <p><strong>Status:</strong> Offen (Pending)</p>
          </div>
          
          <p style="color: #6b7280;">Bitte prüfen Sie den Antrag im Admin-Dashboard.</p>
        </div>
      `;
    }

    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: {
        type: template ? 'custom' : 'bulk',
        ...(template ? {
          to: 'oliver.wagner@gridbalance.de',
          data: {},
          customSubject: subject,
          customHtml: htmlContent
        } : {
          recipients: [
            { 
              email: 'oliver.wagner@gridbalance.de', 
              variables: {} 
            }
          ],
          data: {
            subject,
            htmlContent
          }
        })
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send admin new application notification:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send admin notification when application status changes to redeemed
 * @param {Object} applicationData - Application data
 * @returns {Object} Result of email send operation
 */
export const sendAdminRedeemedNotification = async (applicationData) => {
  try {
    // Fetch voucher code from database if not provided
    let voucherCode = applicationData?.voucherCode;
    
    if (!voucherCode && applicationData?.applicationId) {
      const { data: voucherData, error: voucherError } = await supabase
        ?.from('vouchers')
        ?.select('voucher_code')
        ?.eq('application_id', applicationData?.applicationId)
        ?.single();
      
      if (!voucherError && voucherData) {
        voucherCode = voucherData?.voucher_code;
      }
    }
    
    // Fetch custom template from database
    const template = await fetchEmailTemplate('admin_voucher_redeemed');
    
    let subject = `THG-Gutschein eingelöst - ${applicationData?.customerName}`;
    let htmlContent = '';

    if (template) {
      // Use custom template
      const templateVariables = {
        applicationId: applicationData?.applicationId,
        customerName: applicationData?.customerName,
        customerEmail: applicationData?.customerEmail,
        dealershipName: applicationData?.dealershipName || 'Ihr Partner',
        voucherCode: voucherCode || 'Nicht verfügbar',
        chassisNumber: applicationData?.chassisNumber || 'Nicht erfasst',
        redemptionDate: new Date()?.toLocaleDateString('de-DE', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        })
      };
      subject = replacePlaceholders(template?.subject, templateVariables);
      htmlContent = replacePlaceholders(template?.html_content, templateVariables);
    } else {
      // Fallback to hardcoded template
      htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #16a34a;">Gutschein wurde eingelöst</h2>
          <p>Ein THG-Gutschein wurde vom Partner eingelöst:</p>
          
          <div style="background-color: #f0fdf4; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #16a34a;">
            <h3 style="margin-top: 0; color: #1f2937;">Einlösungsinformationen</h3>
            <p><strong>Antrags-ID:</strong> ${applicationData?.applicationId}</p>
            <p><strong>Kunde:</strong> ${applicationData?.customerName}</p>
            <p><strong>E-Mail:</strong> ${applicationData?.customerEmail}</p>
            <p><strong>Partner:</strong> ${applicationData?.dealershipName || 'Ihr Partner'}</p>
            <p><strong>Gutscheincode:</strong> ${voucherCode || 'Nicht verfügbar'}</p>
            <p><strong>Fahrgestellnummer:</strong> ${applicationData?.chassisNumber || 'Nicht erfasst'}</p>
            <p><strong>Eingelöst am:</strong> ${new Date()?.toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          
          <p style="color: #6b7280; font-size: 14px;">
            Der Gutschein wurde erfolgreich vom Partner verifiziert und eingelöst.
          </p>
        </div>
      `;
    }

    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: {
        type: template ? 'custom' : 'bulk',
        ...(template ? {
          to: 'oliver.wagner@gridbalance.de',
          data: {},
          customSubject: subject,
          customHtml: htmlContent
        } : {
          recipients: [
            { 
              email: 'oliver.wagner@gridbalance.de', 
              variables: {} 
            }
          ],
          data: {
            subject,
            htmlContent
          }
        })
      }
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send admin redeemed notification:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send registration confirmation email
 * @param {Object} confirmationData - Confirmation email data
 * @returns {Object} Result of email send operation
 */
export const sendRegistrationConfirmation = async (confirmationData) => {
  try {
    // Fetch custom template from database
    const template = await fetchEmailTemplate('registration_confirmation');
    
    if (!template) {
      console.error('Template registration_confirmation not found in database');
      return { success: false, error: 'Template not found' };
    }

    // Ensure license plate value is available
    const licensePlateValue = confirmationData?.customerLicensePlate || 'Nicht angegeben';

    const templateVariables = {
      customerName: confirmationData?.customerName || 'Kunde',
      customerEmail: confirmationData?.customerEmail,
      // Provide license plate with multiple possible placeholder names to handle template variations
      customerLicensePlate: licensePlateValue,
      licensePlate: licensePlateValue,
      customer_license_plate: licensePlateValue,
      dealershipName: confirmationData?.dealershipName || 'Ihr Partner',
      confirmationUrl: `${window.location?.origin}/confirm-registration?token=${confirmationData?.confirmationToken}`,
      applicationDate: confirmationData?.applicationDate ? new Date(confirmationData?.applicationDate)?.toLocaleDateString('de-DE') : new Date()?.toLocaleDateString('de-DE')
    };

    // EXTREME DEBUG LOGGING
    console.log('========================================');
    console.log('🔍 REGISTRATION EMAIL DEBUG START');
    console.log('========================================');
    console.log('📥 Input confirmationData:', JSON.stringify(confirmationData, null, 2));
    console.log('🎯 License plate value extracted:', licensePlateValue);
    console.log('📋 All template variables:', JSON.stringify(templateVariables, null, 2));
    console.log('📄 Raw template subject:', template?.subject);
    console.log('📄 Raw template HTML length:', template?.html_content?.length);
    
    // Find and log the exact placeholder in template
    const kennzeichenIndex = template?.html_content?.indexOf('Kennzeichen');
    if (kennzeichenIndex !== -1) {
      const snippet = template?.html_content?.substring(kennzeichenIndex - 50, kennzeichenIndex + 150);
      console.log('📄 Raw template snippet around Kennzeichen:', snippet);
    }

    // Check what placeholders exist in the template
    const placeholderMatches = template?.html_content?.match(/{{\w+}}/g) || [];
    console.log('🔖 All placeholders found in template:', placeholderMatches);

    let subject = replacePlaceholders(template?.subject, templateVariables);
    let htmlContent = replacePlaceholders(template?.html_content, templateVariables);

    // Check the HTML after replacement
    console.log('✅ Subject after replacement:', subject);
    console.log('✅ HTML length after replacement:', htmlContent?.length);
    
    // Find Kennzeichen in the processed HTML
    const processedKennzeichenIndex = htmlContent?.indexOf('Kennzeichen');
    if (processedKennzeichenIndex !== -1) {
      const processedSnippet = htmlContent?.substring(processedKennzeichenIndex - 50, processedKennzeichenIndex + 150);
      console.log('✅ Processed HTML snippet around Kennzeichen:', processedSnippet);
    }

    // Check if any license plate placeholder was NOT replaced
    const unreplacedPlaceholders = [];
    if (htmlContent?.includes('{{customerLicensePlate}}')) {
      unreplacedPlaceholders?.push('{{customerLicensePlate}}');
    }
    if (htmlContent?.includes('{{licensePlate}}')) {
      unreplacedPlaceholders?.push('{{licensePlate}}');
    }
    if (htmlContent?.includes('{{customer_license_plate}}')) {
      unreplacedPlaceholders?.push('{{customer_license_plate}}');
    }

    if (unreplacedPlaceholders?.length > 0) {
      console.error('❌ WARNING: License plate placeholders NOT replaced:', unreplacedPlaceholders);
    } else {
      console.log('✅ All license plate placeholders successfully replaced');
    }

    // Log the exact payload being sent to Edge Function
    const emailPayload = {
      type: 'custom',
      to: confirmationData?.customerEmail,
      customSubject: subject,
      customHtml: htmlContent
    };
    console.log('📤 Payload being sent to Edge Function:', {
      type: emailPayload?.type,
      to: emailPayload?.to,
      subjectLength: emailPayload?.customSubject?.length,
      htmlLength: emailPayload?.customHtml?.length,
      htmlSnippetAroundKennzeichen: emailPayload?.customHtml?.substring(
        Math.max(0, emailPayload?.customHtml?.indexOf('Kennzeichen') - 50),
        emailPayload?.customHtml?.indexOf('Kennzeichen') + 150
      )
    });
    console.log('========================================');
    console.log('🔍 REGISTRATION EMAIL DEBUG END');
    console.log('========================================');

    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: emailPayload
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send registration confirmation email:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send test email to verify SMTP configuration
 * @param {string} recipientEmail - Email address to send test to
 * @returns {Object} Result of email send operation
 */
export const sendTestEmail = async (recipientEmail = 'florian@baier.one') => {
  try {
    console.log('🔍 Starting test email send to:', recipientEmail);
    
    // Check if Supabase client is properly initialized
    if (!supabase) {
      console.error('❌ Supabase client not initialized');
      return { 
        success: false, 
        error: 'Supabase client not initialized',
        message: '❌ Fehler: Supabase-Client nicht initialisiert' 
      };
    }

    if (!supabase?.functions) {
      console.error('❌ Supabase functions not available');
      return { 
        success: false, 
        error: 'Supabase functions not available',
        message: '❌ Fehler: Supabase Edge Functions nicht verfügbar' 
      };
    }

    console.log('✅ Supabase client initialized');
    console.log('🔍 Attempting to invoke send-email Edge Function...');

    const testSubject = 'THG Premium - SMTP Test Email';
    const testHtml = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <h2 style="color: #2563eb;">SMTP Test Email</h2>
        <p>Dies ist eine Test-E-Mail zur Überprüfung der SMTP-Konfiguration.</p>
        
        <div style="background-color: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Test Details:</h3>
          <p><strong>Zeitstempel:</strong> ${new Date()?.toLocaleString('de-DE')}</p>
          <p><strong>Empfänger:</strong> ${recipientEmail}</p>
          <p><strong>System:</strong> THG Premium Platform</p>
        </div>
        
        <div style="background-color: #dbeafe; padding: 15px; border-radius: 8px; border-left: 4px solid #2563eb;">
          <p style="margin: 0;"><strong>✅ Status:</strong> SMTP-Verbindung erfolgreich</p>
          <p style="margin: 10px 0 0 0; font-size: 14px; color: #64748b;">
            Wenn Sie diese E-Mail erhalten, funktioniert die SMTP-Konfiguration korrekt.
          </p>
        </div>
        
        <p style="margin-top: 20px; color: #64748b; font-size: 14px;">
          Diese E-Mail wurde automatisch vom THG Premium System generiert.<br>
          Bei Fragen wenden Sie sich bitte an: thg@gridbalance.de
        </p>
      </div>
    `;

    let response;
    try {
      response = await supabase?.functions?.invoke('send-email', {
        method: 'POST',
        body: {
          type: 'bulk',
          recipients: [
            { 
              email: recipientEmail, 
              variables: {} 
            }
          ],
          data: {
            subject: testSubject,
            htmlContent: testHtml
          }
        }
      });
      
      console.log('📨 Edge Function response received:', response);
    } catch (invokeError) {
      // Handle FunctionsFetchError - network/connection errors
      console.warn('⚠️ Edge Function invocation error (this may be expected if function is not deployed):', invokeError?.name, invokeError?.message);
      
      // Check if it's a FunctionsFetchError (network error)
      if (invokeError?.name === 'FunctionsFetchError' || invokeError?.message?.includes('Failed to send a request')) {
        return { 
          success: false, 
          error: 'Edge Function nicht erreichbar',
          message: `❌ Fehler: Die Edge Function "send-email" konnte nicht erreicht werden.\n\nMögliche Ursachen:\n\n1. ⚠️ Edge Function ist nicht deployed\n   → Bitte prüfen Sie im Supabase Dashboard → Edge Functions\n   → Die Funktion "send-email" muss existieren und deployed sein\n\n2. 🔒 Netzwerkverbindung blockiert\n   → Firewall oder Proxy blockiert Zugriff auf Supabase\n\n3. 📝 Falscher Funktionsname\n   → Funktion heißt möglicherweise anders als "send-email"\n\nNächste Schritte:\n→ Öffnen Sie Ihr Terminal im Projektverzeichnis\n\n2. Deployen Sie die Edge Function:\n   supabase functions deploy send-email\n\n3. Alternativ: Prüfen Sie im Supabase Dashboard\n   → Edge Functions → "send-email" sollte dort erscheinen\n\n4. Falls die Funktion fehlt, prüfen Sie:\n   → Datei existiert: supabase/functions/send-email/index.ts\n   → Supabase CLI ist installiert\n   → Sie sind mit dem richtigen Projekt verbunden\n\n📌 WICHTIG:\nOhne deployed Edge Function können keine E-Mails versendet werden.` 
        };
      }
      
      // Handle FunctionsHttpError - Edge Function returned error status
      let errorMessage = 'Edge Function returned an error';
      let errorDetails = '';
      
      if (invokeError?.context?.body) {
        try {
          const errorBody = JSON.parse(invokeError?.context?.body);
          errorMessage = errorBody?.error || errorMessage;
          errorDetails = errorBody?.details || '';
          
          if (errorBody?.missing) {
            errorDetails += ` Missing: ${errorBody?.missing?.join(', ')}`;
          }
        } catch (parseError) {
          errorMessage = invokeError?.message || errorMessage;
        }
      } else {
        errorMessage = invokeError?.message || errorMessage;
      }
      
      return { 
        success: false, 
        error: errorMessage,
        message: `❌ Fehler: ${errorMessage}${errorDetails ? ' - ' + errorDetails : ''}` 
      };
    }

    // Check for FunctionsFetchError in response.error (not thrown as exception)
    if (response?.error?.name === 'FunctionsFetchError') {
      console.warn('⚠️ FunctionsFetchError detected - Edge Function not deployed (this is expected if function is not yet deployed)');
      return { 
        success: false, 
        error: 'Edge Function nicht deployed',
        message: `❌ FEHLER: Edge Function "send-email" ist nicht erreichbar\n\n🔍 DIAGNOSE:\nDie Edge Function existiert nicht oder ist nicht deployed.\n\n✅ LÖSUNG:\n\n1. Öffnen Sie Ihr Terminal im Projektverzeichnis\n\n2. Deployen Sie die Edge Function:\n   supabase functions deploy send-email\n\n3. Alternativ: Prüfen Sie im Supabase Dashboard\n   → Edge Functions → "send-email" sollte dort erscheinen\n\n4. Falls die Funktion fehlt, prüfen Sie:\n   → Datei existiert: supabase/functions/send-email/index.ts\n   → Supabase CLI ist installiert\n   → Sie sind mit dem richtigen Projekt verbunden\n\n📌 WICHTIG:\nOhne deployed Edge Function können keine E-Mails versendet werden.` 
      };
    }

    // Check for other errors in response
    if (response?.error) {
      console.error('❌ Edge Function error:', response?.error);
      
      // Try to extract detailed error from response data
      let errorMessage = response?.error?.message || 'Unknown error';
      let errorDetails = '';
      
      if (response?.data) {
        errorMessage = response?.data?.error || errorMessage;
        errorDetails = response?.data?.details || '';
        
        if (response?.data?.missing) {
          errorDetails += ` Missing: ${response?.data?.missing?.join(', ')}`;
        }
      }
      
      return { 
        success: false, 
        error: errorMessage,
        message: `❌ Fehler: ${errorMessage}${errorDetails ? ' - ' + errorDetails : ''}` 
      };
    }
    
    console.log('✅ Test email sent successfully to:', recipientEmail);
    return { 
      success: true, 
      message: `✅ Test-E-Mail erfolgreich an ${recipientEmail} gesendet` 
    };
  } catch (error) {
    console.error('❌ Failed to send test email:', error);
    console.error('Error details:', {
      name: error?.name,
      message: error?.message,
      stack: error?.stack
    });
    return { 
      success: false, 
      message: `❌ Fehler beim Senden der Test-E-Mail: ${error?.message}` 
    };
  }
};

/**
 * Test email send functionality
 * @param {string} testEmail - Email address to send test to
 * @param {string} templateType - Optional template type to use (default: 'test')
 * @returns {Object} Result of test email send
 */
export const testEmailSend = async (testEmail, templateType = 'test') => {
  try {
    console.log('[emailService] Sending test email to:', testEmail);
    console.log('[emailService] Using template type:', templateType);
    console.log('[emailService] Request payload:', { type: templateType === 'default' ? 'test' : templateType, to: testEmail });

    // If templateType is 'default', use 'test' type
    const emailType = templateType === 'default' ? 'test' : templateType;

    // For template-based tests, we need to provide sample data
    let requestBody = {
      type: emailType,
      to: testEmail
    };

    // Add sample data for specific template types
    if (emailType !== 'test') {
      requestBody.data = {
        // Sample data for templates
        customerName: 'Max Mustermann',
        customerEmail: testEmail,
        customerPhone: '+49 123 456789',
        dealershipName: 'Partner Beispiel GmbH',
        applicationId: 'TEST-' + Date.now(),
        chassisNumber: 'WVWZZZ1KZBW123456',
        applicationDate: new Date()?.toLocaleDateString('de-DE'),
        status: 'Eingegangen',
        // Add more sample variables as needed
        contactPerson: 'Herr Müller',
        city: 'Berlin',
        landingPageUrl: 'https://www.gridbalance.de/beispiel',
        voucherCheckUrl: 'https://www.gridbalance.de/voucher-check'
      };
    }

    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: requestBody
    });

    console.log('[emailService] Edge Function response:', { data, error });

    if (error) {
      console.error('[emailService] Edge Function error:', error);

      // Extract error message from various possible locations
      let errorMessage = 'Edge Function returned an error';
      
      // Try to get error from context body
      if (error?.context?.body) {
        try {
          const errorBody = typeof error?.context?.body === 'string' 
            ? JSON.parse(error?.context?.body) 
            : error?.context?.body;
          errorMessage = errorBody?.error || errorBody?.message || errorMessage;
        } catch (e) {
          console.error('[emailService] Failed to parse error context:', e);
        }
      }
      
      // Fallback to error message or name
      if (errorMessage === 'Edge Function returned an error') {
        errorMessage = error?.message || error?.name || 'Unknown error from Edge Function';
      }
      
      throw new Error(errorMessage);
    }

    if (!data?.success) {
      const errorDetail = data?.error || 'Unbekannter Fehler';
      console.error('[emailService] Email send failed:', errorDetail);
      throw new Error(errorDetail);
    }

    console.log('[emailService] Test email sent successfully');
    return { 
      success: true, 
      message: '✅ Test-E-Mail erfolgreich gesendet! Überprüfen Sie Ihr Postfach.' 
    };
  } catch (error) {
    console.error('[emailService] Failed to send test email:', error);
    return { 
      success: false, 
      message: `❌ ${error?.message || 'Unbekannter Fehler beim E-Mail-Versand'}`
    };
  }
};

/**
 * Send application submitted email (status changed to processing/beantragt)
 * @param {Object} applicationData - Application data
 * @returns {Object} Result of email send operation
 */
export const sendApplicationSubmittedEmail = async (applicationData) => {
  try {
    console.log('[sendApplicationSubmittedEmail] ========== STARTING SUBMITTED EMAIL WORKFLOW ==========');
    console.log('[sendApplicationSubmittedEmail] Application ID:', applicationData?.applicationId || applicationData?.id);
    console.log('[sendApplicationSubmittedEmail] Customer Email:', applicationData?.customerEmail);

    // Fetch full application data including license plate from database
    const { data: fullAppData, error: fetchError } = await supabase
      ?.from('applications')
      ?.select('customer_license_plate, vehicle_model')
      ?.eq('id', applicationData?.applicationId || applicationData?.id)
      ?.single();

    if (fetchError) {
      console.error('[sendApplicationSubmittedEmail] Error fetching application data:', fetchError);
    }

    // Fetch custom template from database
    console.log('[sendApplicationSubmittedEmail] Fetching email template: application_submitted');
    const template = await fetchEmailTemplate('application_submitted');

    if (!template) {
      console.error('[sendApplicationSubmittedEmail] ❌ TEMPLATE NOT FOUND');
      console.error('[sendApplicationSubmittedEmail] Template type: application_submitted');
      console.error('[sendApplicationSubmittedEmail] Please check:');
      console.error('[sendApplicationSubmittedEmail]   1. Template exists in email_templates table');
      console.error('[sendApplicationSubmittedEmail]   2. Template is_active = true');
      console.error('[sendApplicationSubmittedEmail]   3. Migration 20260212162000_add_application_submitted_email_template.sql was applied');
      return { success: false, error: 'Template not found' };
    }

    console.log('[sendApplicationSubmittedEmail] ✅ Template found:', template?.template_name);

    const templateVariables = {
      applicationId: applicationData?.applicationId || applicationData?.id,
      dealershipName: applicationData?.dealershipName || 'Ihr Partner',
      customerName: applicationData?.customerName || 'Kunde',
      vehicleModel: fullAppData?.vehicle_model || applicationData?.vehicleModel || '',
      customerLicensePlate: fullAppData?.customer_license_plate || applicationData?.customerLicensePlate || 'Nicht angegeben'
    };

    console.log('[sendApplicationSubmittedEmail] Template variables:', JSON.stringify(templateVariables, null, 2));

    let subject = replacePlaceholders(template?.subject, templateVariables);
    let htmlContent = replacePlaceholders(template?.html_content, templateVariables);

    console.log('[sendApplicationSubmittedEmail] Email subject:', subject);
    console.log('[sendApplicationSubmittedEmail] Email recipient:', applicationData?.customerEmail);
    console.log('[sendApplicationSubmittedEmail] Calling edge function: send-email');

    const { data, error } = await supabase?.functions?.invoke('send-email', {
      method: 'POST',
      body: {
        type: 'custom',
        to: applicationData?.customerEmail,
        customSubject: subject,
        customHtml: htmlContent,
        data: templateVariables
      }
    });

    if (error) {
      console.error('[sendApplicationSubmittedEmail] ❌ EDGE FUNCTION ERROR');
      console.error('[sendApplicationSubmittedEmail] Error:', JSON.stringify(error, null, 2));
      throw error;
    }

    console.log('[sendApplicationSubmittedEmail] ✅ ✅ ✅ EMAIL SENT SUCCESSFULLY ✅ ✅ ✅');
    console.log('[sendApplicationSubmittedEmail] Edge function response:', JSON.stringify(data, null, 2));
    console.log('[sendApplicationSubmittedEmail] ========== WORKFLOW END (SUCCESS) ==========');
    return { success: true, data };
  } catch (error) {
    console.error('[sendApplicationSubmittedEmail] ❌ ❌ ❌ FATAL ERROR ❌ ❌ ❌');
    console.error('[sendApplicationSubmittedEmail] Error:', error);
    console.error('[sendApplicationSubmittedEmail] Error message:', error?.message);
    console.error('[sendApplicationSubmittedEmail] ========== WORKFLOW END (FATAL ERROR) ==========');
    return { success: false, error: error?.message };
  }
};